/**
 * Coerce API values (including Mongo extended JSON) to a finite number or NaN.
 * @param {unknown} value
 * @returns {number}
 */
export function toNumericAmount(value) {
  if (value == null) return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  if (typeof value === "string") {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : NaN;
  }
  if (typeof value === "object") {
    if (Object.prototype.hasOwnProperty.call(value, "$numberDecimal")) {
      const raw = value.$numberDecimal;
      const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
      return Number.isFinite(n) ? n : NaN;
    }
    if (Object.prototype.hasOwnProperty.call(value, "$numberInt")) {
      const n = Number(value.$numberInt);
      return Number.isFinite(n) ? n : NaN;
    }
    if (Object.prototype.hasOwnProperty.call(value, "$numberLong")) {
      const n = Number(value.$numberLong);
      return Number.isFinite(n) ? n : NaN;
    }
  }
  return NaN;
}

/**
 * Formats a number as Indian Currency (INR)
 * @param {number|string|object} amount - Plain number, string, or Mongo { $numberDecimal }
 * @returns {string} - Formatted price string (e.g., "₹ 1,23,456")
 */
export const formatPrice = (amount) => {
  if (amount === null || amount === undefined) return "";

  const numAmount = toNumericAmount(amount);
  if (!Number.isFinite(numAmount)) return "";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(numAmount);
};

/**
 * Calculates the percentage discount between an original and current price.
 * @param {number|string|object} current - Current price
 * @param {number|string|object} original - Original price
 * @returns {number|null} - Percentage (e.g., 20) or null if invalid
 */
export const calculateDiscount = (current, original) => {
  const cur = toNumericAmount(current);
  const old = toNumericAmount(original);

  if (!Number.isFinite(cur) || !Number.isFinite(old) || old <= 0 || cur >= old) {
    return null;
  }

  return Math.round(((old - cur) / old) * 100);
};
